import { Autocomplete, Paper, Stack, TextField, Typography } from '@mui/material';
import { compact, filter, find, keys, map, size, uniq } from 'lodash';
import React, { useMemo } from 'react';

import StudyReportSectionTableCard, {
  SectionTableCardSkeleton,
} from 'components/Pages/StudyReport/StudyReportSectionTableCard';
import { SectionTable, TableColumn } from 'interfaces/agentSession';
import { CommonJobResult, ReportJobParquetResult } from 'interfaces/job';
import { JobResultColumnDefinition, JobResultSourceConfig } from 'interfaces/jobResultMetadata';
import { ParquetFile, useParquetFileQueries } from 'utils/useParquetFile';
import { LinksSection } from './LinksSection';

const PARQUET_BOOLEAN_TYPES = ['BOOLEAN'];
const PARQUET_NUMBER_TYPES = ['INT32', 'INT64', 'INT96', 'FLOAT', 'DOUBLE'];
const ISO_DATE_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/;

const normalizeParquetValue = (value: unknown): unknown => {
  if (typeof value === 'bigint') {
    const asNumber = Number(value);
    return Number.isSafeInteger(asNumber) ? asNumber : value.toString();
  }

  return value;
};

const normalizeParquetRow = (row: Record<string, unknown>) =>
  Object.fromEntries(map(Object.entries(row), ([field, value]) => [field, normalizeParquetValue(value)]));

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isDateLikeValue = (value: unknown) =>
  value instanceof Date ||
  (typeof value === 'string' && ISO_DATE_VALUE_REGEX.test(value) && !Number.isNaN(Date.parse(value)));

const inferTableColumnType = ({
  parquetType,
  values,
}: {
  parquetType?: string;
  values: unknown[];
}): TableColumn['type'] => {
  if (parquetType && PARQUET_BOOLEAN_TYPES.includes(parquetType)) {
    return 'boolean';
  }

  if (parquetType && PARQUET_NUMBER_TYPES.includes(parquetType)) {
    return 'number';
  }

  if (!size(values)) {
    return undefined;
  }

  if (values.every((value) => typeof value === 'boolean')) {
    return 'boolean';
  }

  if (values.every((value) => isFiniteNumber(value))) {
    return 'number';
  }

  if (values.every((value) => isDateLikeValue(value))) {
    return 'date';
  }

  return undefined;
};

const buildTableColumnsFromParquetFile = (
  parquetFile: ParquetFile,
  resultColumns?: JobResultColumnDefinition[]
): TableColumn[] => {
  const schemaColumns = filter(parquetFile.metadata?.schema || [], ({ name, type }) => Boolean(name) && Boolean(type));
  const fieldsFromRows = uniq(
    compact(
      map(parquetFile.rows, (row) => keys(row))
        .flat()
        .filter(Boolean)
    )
  );
  const orderedFields = uniq([...map(schemaColumns, 'name'), ...fieldsFromRows]);

  return map(orderedFields, (field) => {
    const schemaColumn = find(schemaColumns, ({ name }) => name === field);
    const resultColumn = find(resultColumns, ({ field: columnField, name, key }) => {
      return columnField === field || name === field || key === field;
    });
    const values = compact(map(parquetFile.rows, (row) => normalizeParquetValue(row[field])));

    return {
      field,
      headerName: resultColumn?.headerName || resultColumn?.name || field,
      type: inferTableColumnType({ parquetType: schemaColumn?.type, values }),
      meta: resultColumn?.meta,
    };
  });
};

export const buildReportJobParquetTable = ({
  parquetFile,
  resultDescription,
  resultKey,
  resultTitle,
  jobId,
  storageUrl,
  resultColumns,
  sourceConfig,
}: {
  parquetFile: ParquetFile;
  resultDescription?: string;
  resultKey: string;
  resultTitle: string;
  jobId: string;
  storageUrl?: string;
  resultColumns?: JobResultColumnDefinition[];
  sourceConfig?: JobResultSourceConfig;
}): SectionTable => ({
  id: `${jobId}-${resultKey}`,
  title: resultTitle,
  description: resultDescription || null,
  columns: buildTableColumnsFromParquetFile(parquetFile, resultColumns),
  rows: map(parquetFile.rows, (row) => normalizeParquetRow(row)),
  sourceType: 'custom',
  sourceConfig: {
    ...(sourceConfig || {}),
    fileType: 'parquet',
    jobId,
    resultKey,
    storageUrl,
  },
  generatedAt: new Date().toISOString(),
});

interface Props {
  resultEntry: ReportJobParquetResult;
  resultTitle: string;
  resultDescription?: string;
  resultKey: string;
  jobId: string;
  studyId?: string;
  links: CommonJobResult['links'];
  selectedGroupResultKey?: string;
  groupResults?: string[];
  groupKeyToTitle?: (key: string) => string;
  setSelectedResult: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const getFetchableUrl = (resultEntry: Pick<ReportJobParquetResult, 'storage_url' | 'url'>) => {
  const candidateUrl = resultEntry.storage_url || resultEntry.url;

  return candidateUrl && !candidateUrl.startsWith('s3://') ? candidateUrl : undefined;
};

const JobResultParquetTable: React.FC<Props> = ({
  resultEntry,
  resultTitle,
  resultDescription,
  resultKey,
  jobId,
  studyId,
  links,
  selectedGroupResultKey,
  groupResults,
  groupKeyToTitle,
  setSelectedResult,
}) => {
  const parquetUrl = getFetchableUrl(resultEntry);
  const parquetQueryInputs = useMemo(
    () => [
      {
        enabled: Boolean(parquetUrl),
        urlForGetRequest: parquetUrl || '',
      },
    ],
    [parquetUrl]
  );
  const [parquetQuery] = useParquetFileQueries(parquetQueryInputs);
  const parquetTable = useMemo(
    () =>
      parquetQuery?.data
        ? buildReportJobParquetTable({
            parquetFile: parquetQuery.data,
            resultDescription,
            resultKey,
            resultTitle,
            jobId,
            storageUrl: parquetUrl,
            resultColumns: resultEntry.columns,
            sourceConfig: resultEntry.sourceConfig,
          })
        : null,
    [
      jobId,
      parquetQuery?.data,
      parquetUrl,
      resultDescription,
      resultEntry.columns,
      resultEntry.sourceConfig,
      resultKey,
      resultTitle,
    ]
  );
  const isLoading = Boolean(parquetUrl) && parquetQuery?.isLoading;
  const showError = !parquetTable && !isLoading && Boolean(resultEntry?.storage_url || resultEntry?.url);
  const beforeGrid =
    size(groupResults) > 1 || links ? (
      <Stack spacing={1}>
        {size(groupResults) > 1 && (
          <Autocomplete<string, false, false, false>
            value={selectedGroupResultKey}
            getOptionLabel={groupKeyToTitle}
            options={groupResults || []}
            onChange={(_event, newSelectedResult) => setSelectedResult(newSelectedResult)}
            clearIcon={false}
            renderInput={(params) => <TextField {...params} variant="standard" label="Table option" />}
          />
        )}
        <LinksSection links={links} studyId={studyId} />
      </Stack>
    ) : undefined;

  if (isLoading) {
    return <SectionTableCardSkeleton />;
  }

  if (showError || !parquetTable) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            {resultTitle}
          </Typography>
          {resultDescription ? (
            <Typography variant="caption" color="text.secondary">
              {resultDescription}
            </Typography>
          ) : null}
          <Typography variant="caption" color="warning.main">
            Table data could not be loaded
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try reopening the result or refreshing the page.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <StudyReportSectionTableCard table={parquetTable} beforeGrid={beforeGrid} sourceLabel={null} studyId={studyId} />
  );
};

export default JobResultParquetTable;
